import { useEffect, useRef, useState } from "react";

/**
 * Splits a display string into its numeric core and surrounding text so
 * values like "50+", "99.9%", "< 2h" or "10x" can animate without losing
 * their formatting.
 *
 * Returns null when there is no number to animate (e.g. "AI-First"), which
 * signals the caller to render the original string untouched.
 */
function parseValue(value) {
  const str = String(value);
  const match = str.match(/-?\d[\d,]*\.?\d*/);
  if (!match) return null;

  const raw = match[0];
  const target = parseFloat(raw.replace(/,/g, ""));
  if (Number.isNaN(target)) return null;

  const decimals = raw.includes(".") ? raw.split(".")[1].length : 0;

  return {
    target,
    decimals,
    useGrouping: raw.includes(","),
    prefix: str.slice(0, match.index),
    suffix: str.slice(match.index + raw.length),
  };
}

const easeOutExpo = (t) => (t === 1 ? 1 : 1 - Math.pow(2, -10 * t));

/**
 * Animates a numeric display value from zero up to its target.
 *
 * Designed to pair with useReveal: pass the reveal's `isVisible` as `trigger`
 * so the count runs when the stat scrolls into view.
 *
 *   const [ref, isVisible] = useReveal();
 *   const value = useCountUp("99.9%", isVisible);
 *
 * The final value renders immediately — rather than counting — when the user
 * prefers reduced motion, when rAF is unavailable (react-snap prerender), or
 * when the string holds no number. In every one of those cases the correct
 * text is on screen, so nothing can be left showing a placeholder zero.
 *
 * @param {string|number} value      Display value, e.g. "50+" or "99.9%".
 * @param {boolean} [trigger=true]   Starts the animation when true.
 * @param {Object} [options]
 * @param {number} [options.duration=1600]  Animation length in ms.
 * @param {number} [options.delay=0]        Delay before counting begins.
 * @returns {string} The current display string, formatted like the input.
 */
export default function useCountUp(value, trigger = true, options = {}) {
  const { duration = 1600, delay = 0 } = options;

  const parsed = parseValue(value);
  const finalText = String(value);

  const [display, setDisplay] = useState(finalText);
  const frameRef = useRef(null);
  const timeoutRef = useRef(null);

  useEffect(() => {
    if (!trigger || !parsed) return;

    if (
      typeof window === "undefined" ||
      typeof window.requestAnimationFrame !== "function"
    ) {
      return;
    }

    const prefersReducedMotion =
      typeof window.matchMedia === "function" &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReducedMotion) return;

    const { target, decimals, useGrouping, prefix, suffix } = parsed;

    const format = (n) =>
      prefix +
      n.toLocaleString("en-US", {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
        useGrouping,
      }) +
      suffix;

    const run = () => {
      const start = performance.now();

      const step = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        setDisplay(format(target * easeOutExpo(progress)));

        if (progress < 1) {
          frameRef.current = requestAnimationFrame(step);
        } else {
          // Snap to the original string so formatting is exact at rest.
          setDisplay(finalText);
        }
      };

      frameRef.current = requestAnimationFrame(step);
    };

    if (delay > 0) {
      timeoutRef.current = setTimeout(run, delay);
    } else {
      run();
    }

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trigger, finalText, duration, delay]);

  return display;
}
